/// <amd-module name="Test/TestProgressReport/TestProgressReport" />
import { AnalyticsHeaderComponent } from "Analytics/Shared/Common/AnalyticsHeaderComponent";
import { AnalyticsPage } from "Analytics/Shared/Common/AnalyticsPage";
import * as React from "react";
import { DetailsWidget } from "Test/Analytics/Widgets/DetailsWidget/DetailsWidget";
import { IOverallStateWidgetProps, OverallStateWidget } from "Test/Analytics/Widgets/OverallStateWidget";
import { PointHistoryWidget } from "Test/Analytics/Widgets/PointHistoryWidget/PointHistoryWidget";
import { isAdvancedTestExtensionEnabled } from "Test/ManageTestPlans/LicenseUtils";
import { BasePageLayoutComponent, IPageLayoutComponentTelemetryProps } from "Test/ManualTestReports/TestReportsCommon/BasePageLayoutComponent";
import { AssetPath, TestProgressFilterFields, TestProgressReportWidgetNames } from "Test/ManualTestReports/TestReportsCommon/Constants";
import { IFilterData, ITestManagementSettings, ITestProgressReportFilterState } from "Test/ManualTestReports/TestReportsCommon/Contracts";
import { ITestProgressReportWidgetProps } from "Test/ManualTestReports/TestReportsCommon/ITestProgressReportWidgetProps";
import * as TelemetryConstants from "Test/ManualTestReports/TestReportsCommon/TelemetryConstants";
import * as TelemetryService from "Test/ManualTestReports/TestReportsCommon/TelemetryService";
import {
    ITestProgressReportFilterService,
    TestProgressFilterServiceEvents
} from "Test/ManualTestReports/TestReportsCommon/TestProgressReportFilterService";
import * as UrlCreator from "Test/ManualTestReports/TestReportsCommon/UrlCreator";
import { TestProgressFilterBar } from "Test/TestProgressReport/Filters/TestProgressFilterBar";
import { OverallStateWidgetPropsCreator } from "Test/Analytics/Widgets/OverallStateWidgetPropsCreator";
import * as Resources from "Test/TestProgressReport/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, IVssComponentProperties } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

export interface ITestProgressReportState {
    filter: Filter;
    showFilters: boolean;
    noTestPlans: boolean;
}

export class TestProgressReport extends BasePageLayoutComponent<IVssComponentProperties, ITestProgressReportState> {
    private _overallStateWidgetProps: ObservableValue<IOverallStateWidgetProps> = new ObservableValue({} as IOverallStateWidgetProps);
    private _overallStateWidgetPropsCreator: OverallStateWidgetPropsCreator;
    private _initializationPromises: Promise<any>[] = [];
    private _testProgressFilterService: ITestProgressReportFilterService;
    private readonly c_testPlanSettingsDataProviderId = "ms.vss-test-analytics-web.testplan-settings-provider";
    private _filter: Filter;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);
        this._testProgressFilterService = context.pageContext.getService<ITestProgressReportFilterService>("ITestProgressReportFilterService");
        this._setOverallStateWidgetProps({ loading: true } as IOverallStateWidgetProps);
    }

    public componentWillMount() {
        this._testProgressFilterService.subscribe(this._onFilterUpdated, TestProgressFilterServiceEvents.FilterUpdated);
        this._filter = new Filter();
        this.setState({ showFilters: false, filter: this._filter });
        this._initializeWidgets();
        this._initializeFilterState();
    }

    public componentWillUnmount() {
        this._testProgressFilterService.unsubscribe(this._onFilterUpdated, TestProgressFilterServiceEvents.FilterUpdated);
    }

    public componentDidMount() {
        super.componentDidMount();
        TelemetryService.publishEvent(this.context.pageContext, TelemetryConstants.Feature_ProgressReport, {
            lastSelectedPlanId: this._getLastSelectedPlanId(),
            event: TelemetryConstants.Event_PageLoaded
        });
    }

    public render() {
        const elementToDisplay = (): JSX.Element => {
            if (this.state.noTestPlans === true) {
                return this._getZeroTestPlansExperience();
            } else {
                return (
                    <div>
                        <TestProgressFilterBar
                            showFilters={this.state.showFilters}
                            onFilterUpdated={this._onFilterUpdated}
                            filter={this.state.filter}
                        />
                        <div className="overall-state-and-point-history flex-row">
                            <Observer overallStateWidgetProps={this._overallStateWidgetProps}>
                                {(props: { overallStateWidgetProps: IOverallStateWidgetProps }) => {
                                    return React.createElement(OverallStateWidget, props.overallStateWidgetProps);
                                }}
                            </Observer>
                            <PointHistoryWidget {...this._getTestProgressReportWidgetProps(TestProgressReportWidgetNames.PointHistoryWidget)} />
                        </div>
                        <DetailsWidget {...this._getTestProgressReportWidgetProps(TestProgressReportWidgetNames.DetailsWidget)} />
                    </div>
                );
            }
        };

        return (
            <AnalyticsPage renderHeader={this._renderHeader} getDataValidationPromises={this._getDataValidationPromises} onError={() => { }}>
                {elementToDisplay()}
            </AnalyticsPage>
        );
    }

    protected getTelemetryProperties(): IPageLayoutComponentTelemetryProps {
        const widgetNameToPerfTelemetryName: { [key: string]: string } = {};

        widgetNameToPerfTelemetryName[TestProgressReportWidgetNames.DetailsWidget] = TelemetryConstants.TestProgressReportDetailsWidget;
        widgetNameToPerfTelemetryName[TestProgressReportWidgetNames.OverallStateWidget] = TelemetryConstants.TestProgressReportOverallStateWidget;
        widgetNameToPerfTelemetryName[TestProgressReportWidgetNames.PointHistoryWidget] = TelemetryConstants.TestProgressReportPointHistoryWidget;

        return {
            area: TelemetryConstants.Area,
            pageLayoutTelemetryName: TelemetryConstants.TestProgressReportMainComponent,
            pageTelemetryName: TelemetryConstants.TestProgressReportPage,
            widgetNameToPerfTelemetryName: widgetNameToPerfTelemetryName
        };
    }

    private _getZeroTestPlansExperience(): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, AssetPath.NoTestPlansImagePath);

        if (isAdvancedTestExtensionEnabled(this.context.pageContext)) {
            return (
                <ZeroData
                    imagePath={image}
                    imageAltText={Resources.NoTestPlansFound_PrimaryText}
                    primaryText={Resources.NoTestPlansFound_PrimaryText}
                    secondaryText={Resources.NoTestPlansFound_SecondaryText}
                    actionType={ZeroDataActionType.ctaButton}
                    actionText={Resources.CreateTestPlan}
                    onActionClick={e => this._openNewTestPlanUrl(e)}
                />
            );
        } else {
            return (
                <ZeroData
                    imagePath={image}
                    imageAltText={Resources.NoTestPlansFound_PrimaryText}
                    primaryText={Resources.NoTestPlansFound_PrimaryText}
                    secondaryText={Resources.NoTestPlansFound_SecondaryText}
                />
            );
        }
    }

    private _openNewTestPlanUrl(event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
        const link = UrlCreator.getNewTestPlanUrl(this.context.pageContext);
        onClickFPS(this.context.pageContext, link, true, event);
    }

    private _initializeWidgets() {
        this._overallStateWidgetPropsCreator = new OverallStateWidgetPropsCreator("overallStateWidget", this.context.pageContext);
        const overallStateWidgetInitializationPromise = this._overallStateWidgetPropsCreator.getOverallStateWidgetProps(
            undefined,
            this._getLastSelectedPlanId()
        );
        overallStateWidgetInitializationPromise.then(
            (props: IOverallStateWidgetProps) => {
                this._setOverallStateWidgetProps(props);
            },
            (e: Error) => {
                this._setOverallStateWidgetProps({ errorText: e.message } as IOverallStateWidgetProps);
            }
        );
    }

    private _initializeFilterState() {
        const queryTestPlansPromise = this._overallStateWidgetPropsCreator.queryAllTestPlans();
        this._initializationPromises.push(queryTestPlansPromise);

        // Add new promise to _initializationPromises to make sure we don't show loading widgets if there is no test plan.
        let noTestPlanCheckPromiseResolve: any;
        const noTestPlanCheckPromise: Promise<any> = new Promise<any>((resolve, reject) => (noTestPlanCheckPromiseResolve = resolve));
        this._initializationPromises.push(noTestPlanCheckPromise);

        const setDefaultFilterState = () => {
            // no-op
        };

        queryTestPlansPromise.then(
            (testPlanIds: number[]) => {
                if (!testPlanIds || testPlanIds.length === 0) {
                    this.setState({ noTestPlans: true });
                }

                // Resolve noTestPlanCheckPromise to indicate initialize completed.
                noTestPlanCheckPromiseResolve();

                const _lastSelectedPlan = this._getLastSelectedPlanId();

                const defaultState: IFilterState = {};
                if (_lastSelectedPlan && testPlanIds.indexOf(_lastSelectedPlan) >= 0) {
                    defaultState[TestProgressFilterFields.TestPlan] = { value: [_lastSelectedPlan.toString()] };
                } else {
                    if (testPlanIds && testPlanIds.length > 0) {
                        defaultState[TestProgressFilterFields.TestPlan] = { value: [testPlanIds[0].toString()] };
                    }
                }
                if (defaultState[TestProgressFilterFields.TestPlan]) {
                    this._filter.setDefaultState(defaultState);

                    this.setState({
                        showFilters: true,
                        filter: this._filter
                    });
                } else {
                    setDefaultFilterState();
                }
            },
            () => {
                setDefaultFilterState();
                // Resolve noTestPlanCheckPromise to indicate initialize completed.
                noTestPlanCheckPromiseResolve();
            }
        );
    }

    private _onFilterUpdated = (filterData: IFilterData) => {
        this._setOverallStateWidgetProps({ loading: true } as IOverallStateWidgetProps);

        this._overallStateWidgetPropsCreator.getOverallStateWidgetProps(filterData.filterState).then(
            props => {
                this._setOverallStateWidgetProps(props);
            },
            (e: Error) => {
                this._setOverallStateWidgetProps({ errorText: e.message } as IOverallStateWidgetProps);
            }
        );

        TelemetryService.publishEvent(
            this.context.pageContext,
            TelemetryConstants.Feature_ProgressReport,
            {
                event: TelemetryConstants.Event_FilterChange
            },
            filterData.filterState
        );
    };

    private _getDataValidationPromises: () => Promise<any>[] = () => {
        // As AnalyticsPage uses promise.race(dataValidationPromises),
        // pass single promise which resolves after all the _initializationPromises completes.
        return [Promise.all(this._initializationPromises)];
    };

    private _renderHeader = () => {
        return <AnalyticsHeaderComponent title={Resources.ReportTitle} showCommandBar={true} commandBarItems={this._getCommandBarItems()} />;
    };

    private _getCommandBarItems(): IHeaderCommandBarItem[] {
        const isFilterApplied = this.state.showFilters || (!!this.state.filter && !!this.state.filter.getDefaultState()); // Filter is applied if the filter bar is shown or if there is a default state present
        return [
            {
                id: "filter-toggle",
                iconProps: {
                    iconName: isFilterApplied ? "FilterSolid" : "Filter"
                },
                ariaLabel: this.state.showFilters ? Resources.HideFilters : Resources.ShowFilters,
                tooltipProps: {
                    text: this.state.showFilters ? Resources.HideFilters : Resources.ShowFilters
                },
                className: "filter-icon",
                subtle: true,
                onActivate: () => this._onFilterActivated()
            }
        ];
    }

    private _onFilterActivated = (): void => {
        this.setState({
            showFilters: !this.state.showFilters
        });
    };

    private _getLastSelectedPlanId(): number {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const testManagementSettingsData: ITestManagementSettings = contributionService.getData<ITestManagementSettings>(
            this.c_testPlanSettingsDataProviderId
        )!;
        return testManagementSettingsData.selectedPlanId;
    }

    private _setOverallStateWidgetProps(overallStateWidgetProps: IOverallStateWidgetProps) {
        overallStateWidgetProps = {
            ...overallStateWidgetProps,
            ...this._getTestProgressReportWidgetProps(TestProgressReportWidgetNames.OverallStateWidget)
        };
        this._overallStateWidgetProps.value = overallStateWidgetProps;
        TelemetryService.publishEvent(this.context.pageContext, TelemetryConstants.Feature_ProgressReport, {
            errorText: overallStateWidgetProps.errorText,
            event: TelemetryConstants.Event_SummaryWidgetLoaded
        });
    }

    private _getTestProgressReportWidgetProps(widgetName: string): ITestProgressReportWidgetProps {
        return {
            widgetName: widgetName,
            onRenderingStart: this.onWidgetRenderingStart,
            onRenderingEnd: this.onWidgetRenderingEnd
        };
    }
}

Components.add("testProgressReport", TestProgressReport);
